/**
 * Documento e janela de um nó, para os balões e menus do kit.
 *
 * Eles montam-se no `body` e leem a viewport, o `requestAnimationFrame`, o
 * `IntersectionObserver` e os eventos da janela onde a âncora está — que não é
 * sempre a global: no mini-monitor destacado (Document Picture-in-Picture) as
 * âncoras vivem noutra janela, e montar no `body` global desenhava o balão na
 * página principal, longe delas. Na página principal o documento da âncora é o
 * global e nada muda.
 *
 * Resolver no momento do uso, nunca guardar o resultado: a âncora pode mudar de
 * documento enquanto a diretiva vive. Quem precisa de se soltar do mesmo sítio
 * onde se prendeu guarda o `body`/janela que usou, não o que a âncora diz agora.
 *
 * As duas funções devolvem `null` em vez de cair no `document`/`window`
 * globais: o recurso é de quem chama — umas diretivas caem no global, outras no
 * `DOCUMENT` injetado.
 */

/**
 * Documento onde o nó está.
 *
 * @returns `null` só quando não há nó.
 */
export function documentOf(node: Node | null | undefined): Document | null {
  return node?.ownerDocument ?? null;
}

/**
 * Janela do documento onde o nó está. Numa janela destacada tem viewport e
 * `requestAnimationFrame` próprios — o da página principal para de correr
 * quando a aba fica em segundo plano, que é justamente quando a janela
 * destacada está à vista.
 *
 * @returns `null` sem nó, ou quando o documento não tem janela (criado à parte,
 * ou já desligado da sua).
 */
export function windowOf(
  node: Node | null | undefined
): (Window & typeof globalThis) | null {
  return documentOf(node)?.defaultView ?? null;
}
